import { useState, useEffect, useRef, useCallback, useId } from "react";
import { supabase } from "@/integrations/supabase/client";
import { WhatsappLogo, LinkedinLogo, Link as LinkIcon } from "@phosphor-icons/react";
import { useReveal } from "@/hooks/useReveal";
import PublicNav from "@/components/public/PublicNav";
import BottomBar from "@/components/public/BottomBar";
import PublicFooter from "@/components/public/PublicFooter";
import { useLang } from "@/i18n/LanguageContext";
import { priceFor } from "@/i18n/dict";
import { PageMeta } from "@/components/PageMeta";

/* ─────────────────────────────────────────────────────────
   TIPOS E CONSTANTES
───────────────────────────────────────────────────────────*/
const INK = "#0A0A0A";
const BG = "#F7F6F2";
const ACCENT = "#0057FF";
const MUTED = "rgba(10,10,10,0.62)";
const BODY = "rgba(10,10,10,0.78)";
const BORDER = "0.5px solid rgba(10,10,10,0.12)";
const BORDER_MID = "0.5px solid rgba(10,10,10,0.25)";
const MONO = "'IBM Plex Mono', monospace";

const DS_COLORS = ["#0057FF", "#00E87A", "#F0E614", "#FF3F2D", "#FFFFFF"];

const LOGO_FONTS = [
  { f: "'IBM Plex Mono',monospace", w: "400", s: "normal" },
  { f: "'IBM Plex Mono',monospace", w: "400", s: "normal" },
  { f: "'Abril Fatface',serif", w: "400", s: "normal" },
  { f: "'Playfair Display',serif", w: "900", s: "italic" },
  { f: "'Anton',sans-serif", w: "400", s: "normal" },
  { f: "'Bebas Neue',sans-serif", w: "400", s: "normal" },
  { f: "'Bungee',sans-serif", w: "400", s: "normal" },
  { f: "'Archivo Black',sans-serif", w: "400", s: "normal" },
  { f: "'Russo One',sans-serif", w: "400", s: "normal" },
  { f: "'Unbounded',sans-serif", w: "900", s: "normal" },
  { f: "'Big Shoulders Display',sans-serif", w: "900", s: "normal" },
  { f: "'Cinzel',serif", w: "900", s: "normal" },
  { f: "'Monoton',cursive", w: "400", s: "normal" },
  { f: "'Orbitron',sans-serif", w: "900", s: "normal" },
  { f: "'Press Start 2P',cursive", w: "400", s: "normal" },
  { f: "'VT323',monospace", w: "400", s: "normal" },
  { f: "'Manufacturing Consent',serif", w: "400", s: "normal" },
  { f: "'Dancing Script',cursive", w: "700", s: "normal" },
  { f: "'Pacifico',cursive", w: "400", s: "normal" },
  { f: "'Lobster',cursive", w: "400", s: "normal" },
  { f: "'Caveat',cursive", w: "700", s: "normal" },
  { f: "'Space Mono',monospace", w: "700", s: "italic" },
  { f: "'IBM Plex Mono',monospace", w: "400", s: "normal" },
];

const GEO_SHAPES: Array<(sz: number, sw: number, c: string) => string> = [
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><polygon points="50,5 95,95 5,95" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><polygon points="50,5 5,50 50,95 95,50" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><line x1="5" y1="50" x2="95" y2="50" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><line x1="50" y1="5" x2="50" y2="95" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><line x1="5" y1="5" x2="95" y2="95" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><line x1="95" y1="5" x2="5" y2="95" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M5,95 Q50,-30 95,95" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M5,50 Q50,5 95,50 Q50,95 5,50" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><ellipse cx="50" cy="50" rx="45" ry="25" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><polygon points="50,5 61,40 95,40 68,60 79,95 50,72 21,95 32,60 5,40 39,40" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><polygon points="25,5 75,5 95,50 75,95 25,95 5,50" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M50,5 L95,50 L50,95 L5,50 Z M50,25 L75,50 L50,75 L25,50 Z" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="none" stroke="${c}" stroke-width="${sw}"/><circle cx="50" cy="50" r="25" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M5,5 L95,5 L95,95" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M5,95 Q5,5 95,5" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M5,50 C5,20 40,5 50,5 C60,5 95,20 95,50 C95,80 60,95 50,95 C40,95 5,80 5,50" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><polygon points="50,5 20,95 95,38 5,38 80,95" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" rx="45" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M50,5 L95,27.5 L95,72.5 L50,95 L5,72.5 L5,27.5 Z" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M25,5 L75,5 L95,50 L75,95 L25,95 L5,50 Z" fill="none" stroke="${c}" stroke-width="${sw}"/><line x1="50" y1="5" x2="50" y2="95" stroke="${c}" stroke-width="${sw / 2}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="none" stroke="${c}" stroke-width="${sw}"/><line x1="50" y1="5" x2="50" y2="95" stroke="${c}" stroke-width="${sw / 2}"/><line x1="5" y1="50" x2="95" y2="50" stroke="${c}" stroke-width="${sw / 2}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100">${[20, 50, 80].map((x: number) => [20, 50, 80].map((y: number) => `<circle cx="${x}" cy="${y}" r="${sw}" fill="${c}"/>`).join("")).join("")}</svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M5,50 Q30,5 50,50 Q70,95 95,50" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><path d="M5,95 L35,5 L65,95 L95,5" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" fill="none" stroke="${c}" stroke-width="${sw}"/><line x1="5" y1="5" x2="95" y2="95" stroke="${c}" stroke-width="${sw / 2}"/><line x1="95" y1="5" x2="5" y2="95" stroke="${c}" stroke-width="${sw / 2}"/></svg>`,
  (sz, sw, c) =>
    `<svg width="${sz}" height="${sz}" viewBox="0 0 100 100"><polygon points="50,5 95,95 5,95" fill="none" stroke="${c}" stroke-width="${sw}"/><polygon points="50,95 5,5 95,5" fill="none" stroke="${c}" stroke-width="${sw}"/></svg>`,
];

/* ─────────────────────────────────────────────────────────
   HERO SECTION — formas geométricas + letras com hover
───────────────────────────────────────────────────────────*/
function HeroSection({ waitlistCount = 0 }: { waitlistCount?: number }) {
  const { t } = useLang();
  const containerRef = useRef<HTMLElement>(null);
  const floatRef = useRef<HTMLDivElement>(null);
  const poolRef = useRef<HTMLDivElement[]>([]);
  const lastSpawnRef = useRef(0);
  const intervalsRef = useRef<Map<HTMLSpanElement, ReturnType<typeof setInterval>>>(new Map());

  const spawnShape = useCallback((x: number, y: number) => {
    if (!floatRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (y < rect.top || y > rect.bottom || x < rect.left || x > rect.right) return;
    const now = Date.now();
    if (now - lastSpawnRef.current < 60) return;
    lastSpawnRef.current = now;

    const el = document.createElement("div");
    el.style.cssText =
      "position:absolute;opacity:0;pointer-events:none;transform:translate(-50%,-50%) scale(0.3);transition:opacity 0.3s ease,transform 0.4s cubic-bezier(0.175,0.885,0.32,1.275);will-change:transform,opacity;";
    el.style.left = x + (Math.random() - 0.5) * 140 - rect.left + "px";
    el.style.top = y + (Math.random() - 0.5) * 90 - rect.top + "px";

    const fn = GEO_SHAPES[Math.floor(Math.random() * GEO_SHAPES.length)];
    const sz = Math.round(20 + Math.random() * 220);
    const sw = 7 + Math.random() * 15;
    const c = DS_COLORS[Math.floor(Math.random() * DS_COLORS.length)];
    const rot = Math.random() * 360;
    el.style.transform = `translate(-50%,-50%) scale(0.3) rotate(${rot}deg)`;
    el.innerHTML = fn(sz, sw, c);
    floatRef.current.appendChild(el);
    poolRef.current.push(el);

    requestAnimationFrame(() => {
      el.style.transform = `translate(-50%,-50%) scale(1) rotate(${rot}deg)`;
      el.style.opacity = "1";
    });

    const life = 500 + Math.random() * 900;
    setTimeout(() => {
      el.style.opacity = "0";
      el.style.transform = `translate(-50%,-50%) scale(1.3) rotate(${rot}deg)`;
      setTimeout(() => {
        el.remove();
        poolRef.current = poolRef.current.filter((e) => e !== el);
      }, 350);
    }, life);

    if (poolRef.current.length > 18) {
      const old = poolRef.current.shift();
      if (old) {
        old.style.opacity = "0";
        setTimeout(() => old.remove(), 350);
      }
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => spawnShape(e.clientX, e.clientY);
    const touchHandler = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch) spawnShape(touch.clientX, touch.clientY);
    };
    document.addEventListener("mousemove", handler);
    document.addEventListener("touchmove", touchHandler, { passive: true });
    return () => {
      document.removeEventListener("mousemove", handler);
      document.removeEventListener("touchmove", touchHandler);
    };
  }, [spawnShape]);

  const handleCharEnter = (el: HTMLSpanElement) => {
    if (intervalsRef.current.has(el)) return;
    let i = 0;
    const interval = setInterval(() => {
      const font = LOGO_FONTS[i % LOGO_FONTS.length];
      el.style.fontFamily = font.f;
      el.style.fontWeight = font.w;
      el.style.fontStyle = font.s;
      i++;
    }, 60);
    intervalsRef.current.set(el, interval);
  };

  const handleCharLeave = (el: HTMLSpanElement) => {
    const iv = intervalsRef.current.get(el);
    if (iv) {
      clearInterval(iv);
      intervalsRef.current.delete(el);
    }
    el.style.fontFamily = MONO;
    el.style.fontWeight = "400";
    el.style.fontStyle = "normal";
  };

  return (
    <section
      ref={containerRef}
      id="hero"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        padding: "9rem 2.5rem 7rem",
        position: "relative",
        background: BG,
        overflow: "clip",
      }}
    >
      <div ref={floatRef} style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }} />

      {/* Logo interativo */}
      <h1
        aria-label="CRIAAA"
        style={{
          fontSize: "clamp(48px, 12vw, 140px)",
          fontWeight: 400,
          letterSpacing: "0.09em",
          lineHeight: 1,
          margin: 0,
          position: "relative",
          zIndex: 1,
          height: "clamp(48px, 12vw, 140px)",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {["C", "R", "I", "A", "A", "A"].map((char, i) => (
          <span
            key={i}
            aria-hidden="true"
            onMouseEnter={(e) => handleCharEnter(e.currentTarget as HTMLSpanElement)}
            onMouseLeave={(e) => handleCharLeave(e.currentTarget as HTMLSpanElement)}
            style={{
              display: "inline-block",
              color: INK,
              fontFamily: MONO,
              fontWeight: 400,
              fontStyle: "normal",
              lineHeight: 1,
              cursor: "default",
              transition: "font-family 0.02s, font-weight 0.02s, font-style 0.02s",
            }}
          >
            {char}
          </span>
        ))}
      </h1>

      <p
        style={{
          fontFamily: MONO,
          fontSize: 14,
          fontWeight: 300,
          lineHeight: 2,
          color: BODY,
          marginTop: 32,
          marginBottom: 48,
          position: "relative",
          zIndex: 1,
        }}
      >
        {t('landing.heroSub')}
      </p>

      <div className="hide-mobile" style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 420 }}>
        <WaitlistForm waitlistCount={waitlistCount} />
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────
   WAITLIST FORM — 3 etapas: email → modal → confirmação
───────────────────────────────────────────────────────────*/
function WaitlistForm({ waitlistCount = 0 }: { waitlistCount?: number }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<"idle" | "modal" | "done">("idle");
  const [submitting, setSubmitting] = useState(false);
  const [profession, setProfession] = useState("");
  const [portfolioUrl, setPortfolioUrl] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [referredBy] = useState(() =>
    new URLSearchParams(window.location.search).get("invite") || ""
  );

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setSubmitting(true);
    const { error } = await supabase.from("waitlist").insert({
      email: trimmed,
      ...(referredBy ? { referred_by: referredBy } : {}),
    } as any);
    if (!error || error.code === "23505") {
      let realCode = "";
      try {
        const { data } = await supabase
          .from("waitlist")
          .select("referral_code")
          .eq("email", trimmed)
          .single();
        if (data?.referral_code) realCode = data.referral_code;
      } catch {}
      setInviteCode(realCode || Math.random().toString(36).substring(2, 8));
      supabase.functions
        .invoke("send-transactional-email", {
          body: { type: "waitlist-confirmation", data: { to: trimmed } },
        })
        .catch(() => {});
      setStep("modal");
    }
    setSubmitting(false);
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (profession || portfolioUrl) {
      try {
        await supabase
          .from("waitlist")
          .update({ profession, portfolio_url: portfolioUrl } as any)
          .eq("email", email.trim().toLowerCase());
      } catch {}
    }
    setStep("done");
  };

  if (step === "done") {
    const shareUrl = `https://criaaa.com/?invite=${inviteCode}`;
    const shareText = t('landing.waitlist.done.share_text');
    return (
      <div style={{ fontFamily: MONO, textAlign: "left" }}>
        <p style={{ fontSize: 22, fontWeight: 400, color: INK, marginBottom: 8, letterSpacing: "-0.02em" }}>
          {t('landing.waitlist.done.title')}
        </p>
        <p style={{ fontSize: 12, fontWeight: 300, color: MUTED, lineHeight: 1.8, marginBottom: 32, letterSpacing: "0.04em" }}>
          {t('landing.waitlist.done.sub')}
        </p>
        {inviteCode && (
          <div style={{ border: BORDER_MID, padding: "24px" }}>
            <p style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: MUTED, marginBottom: 16 }}>
              {t('landing.waitlist.done.invite_label')}
            </p>
            <p style={{ fontSize: 14, fontWeight: 400, color: INK, marginBottom: 4, wordBreak: "break-all" }}>
              criaaa.com/?invite={inviteCode}
            </p>
            <p style={{ fontSize: 12, color: MUTED, letterSpacing: "0.1em", marginBottom: 24 }}>
              {t('landing.waitlist.done.invite_help')}
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontFamily: MONO, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", padding: "10px 20px", border: `0.5px solid ${INK}`, color: INK, background: "transparent", textDecoration: "none", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 8, borderRadius: 0 }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = INK; (e.currentTarget as HTMLAnchorElement).style.color = BG; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = "transparent"; (e.currentTarget as HTMLAnchorElement).style.color = INK; }}
              >
                <WhatsappLogo size={14} weight="thin" />
                {t('common.whatsapp')}
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontFamily: MONO, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", padding: "10px 20px", border: `0.5px solid ${INK}`, color: INK, background: "transparent", textDecoration: "none", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 8, borderRadius: 0 }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = INK; (e.currentTarget as HTMLAnchorElement).style.color = BG; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = "transparent"; (e.currentTarget as HTMLAnchorElement).style.color = INK; }}
              >
                <LinkedinLogo size={14} weight="thin" />
                {t('common.linkedin')}
              </a>
              <button
                onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                style={{ fontFamily: MONO, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", padding: "10px 20px", border: `0.5px solid ${INK}`, color: INK, background: "transparent", cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 8, borderRadius: 0 }}
                onMouseEnter={e => { e.currentTarget.style.background = INK; e.currentTarget.style.color = BG; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = INK; }}
              >
                <LinkIcon size={14} weight="thin" />
                {copied ? t('common.copied') : t('common.copy_link')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (step === "modal") {
    return (
      <form
        onSubmit={handleModalSubmit}
        style={{ fontFamily: MONO, display: "flex", flexDirection: "column", gap: 24 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: MUTED }}>
            {t('landing.waitlist.modal.profession_label')}
          </label>
          <select
            value={profession}
            onChange={(e) => setProfession(e.target.value)}
            style={{
              fontFamily: MONO,
              fontSize: 14,
              fontWeight: 300,
              background: "transparent",
              border: `0.5px solid ${INK}`,
              borderRadius: 0,
              padding: "12px 16px",
              color: profession ? INK : MUTED,
              outline: "none",
              cursor: "pointer",
              appearance: "none" as const,
              WebkitAppearance: "none" as const,
            }}
          >
            <option value="">{t('landing.waitlist.modal.profession_placeholder')}</option>
            <option value="design">{t('landing.waitlist.modal.professions.design')}</option>
            <option value="fotografia">{t('landing.waitlist.modal.professions.fotografia')}</option>
            <option value="ilustracao">{t('landing.waitlist.modal.professions.ilustracao')}</option>
            <option value="motion">{t('landing.waitlist.modal.professions.motion')}</option>
            <option value="arquitetura">{t('landing.waitlist.modal.professions.arquitetura')}</option>
            <option value="3d">{t('landing.waitlist.modal.professions.3d')}</option>
            <option value="outro">{t('landing.waitlist.modal.professions.outro')}</option>
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: MUTED }}>
            {t('landing.waitlist.modal.portfolio_label')}
          </label>
          <input
            type="url"
            placeholder="https://"
            value={portfolioUrl}
            onChange={(e) => setPortfolioUrl(e.target.value)}
            style={{
              fontFamily: MONO,
              fontSize: 14,
              fontWeight: 300,
              background: "transparent",
              border: `0.5px solid ${INK}`,
              borderRadius: 0,
              padding: "12px 16px",
              color: INK,
              outline: "none",
            }}
          />
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            type="submit"
            style={{
              fontFamily: MONO,
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 400,
              padding: "14px 24px",
              border: "none",
              background: INK,
              color: BG,
              cursor: "pointer",
              flex: 1,
            }}
          >
            {t('landing.waitlist.modal.confirm')}
          </button>
          <button
            type="button"
            onClick={() => setStep("done")}
            style={{
              fontFamily: MONO,
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 400,
              padding: "14px 24px",
              border: BORDER_MID,
              background: "transparent",
              color: MUTED,
              cursor: "pointer",
            }}
          >
            {t('landing.waitlist.modal.skip')}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleEmailSubmit} style={{ fontFamily: MONO }}>
      <div
        style={{
          display: "flex",
          border: `0.5px solid ${INK}`,
          background: "rgba(247,246,242,0.72)",
          backdropFilter: "blur(20px) saturate(160%)",
          WebkitBackdropFilter: "blur(20px) saturate(160%)",
        }}
      >
        <input
          type="email"
          aria-label={t('bottomBar.placeholder')}
          placeholder={t('bottomBar.placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          style={{
            flex: 1,
            fontFamily: MONO,
            fontSize: 14,
            fontWeight: 300,
            background: "transparent",
            border: "none",
            padding: "14px 20px",
            color: INK,
            outline: "none",
          }}
        />
        <button
          type="submit"
          disabled={submitting}
          style={{
            fontFamily: MONO,
            fontSize: 12,
            letterSpacing: "0.18em",
            textTransform: "uppercase" as const,
            fontWeight: 400,
            padding: "14px 24px",
            border: "none",
            borderLeft: `0.5px solid ${INK}`,
            background: submitting ? "rgba(10,10,10,0.5)" : INK,
            color: BG,
            cursor: submitting ? "default" : "pointer",
            whiteSpace: "nowrap" as const,
            transition: "background 0.15s",
          }}
        >
          {submitting ? "..." : t('landing.waitlist.submit')}
        </button>
      </div>
      {waitlistCount > 0 && (
        <p
          style={{
            fontFamily: MONO,
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase" as const,
            color: MUTED,
            marginTop: 12,
            textAlign: "center" as const,
            position: "relative",
            zIndex: 2,
            WebkitTransform: "translateZ(0)",
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 400, letterSpacing: "-0.01em", color: INK, marginRight: 6 }}>
            {waitlistCount}
          </span>
          {t('landing.waitlist.count_suffix')}
        </p>
      )}
    </form>
  );
}

/* ─────────────────────────────────────────────────────────
   TICKER
───────────────────────────────────────────────────────────*/
function Ticker() {
  const { t } = useLang();
  const items = t('landing.ticker') as string[];
  const doubled = [...items, ...items];
  return (
    <div style={{ borderTop: BORDER, borderBottom: BORDER, overflow: "hidden", background: BG }}>
      <style>{`
        @keyframes criaaa-ticker { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        .criaaa-ticker-track { animation: criaaa-ticker 30s linear infinite; display:flex; width:max-content; }
        .criaaa-ticker-outer:hover .criaaa-ticker-track { animation-play-state: paused; }
        @keyframes geo-form-1 {
          from { opacity: 0; transform: scale(0.85); }
          to   { opacity: 1; transform: scale(1); }
        }
        .geo-shape {
          animation: geo-form-1 0.5s ease forwards;
          opacity: 0;
        }
      `}</style>
      <div className="criaaa-ticker-outer" style={{ overflow: "hidden" }}>
        <div className="criaaa-ticker-track">
          {doubled.map((item, i) => (
            <span
              key={i}
              style={{
                fontFamily: MONO,
                fontSize: 12,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: MUTED,
                padding: "12px 32px",
                borderRight: BORDER,
                whiteSpace: "nowrap",
              }}
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   FAQ ITEM
───────────────────────────────────────────────────────────*/
function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div style={{ borderBottom: BORDER }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 0",
          width: "100%",
          background: "transparent",
          border: "none",
          borderRadius: 0,
          cursor: "pointer",
          fontFamily: MONO,
          textAlign: "left",
          color: "inherit",
        }}
      >
        <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 300, color: INK }}>{q}</span>
        <span
          aria-hidden="true"
          style={{
            color: MUTED,
            fontSize: 18,
            transition: "transform 0.2s",
            transform: open ? "rotate(45deg)" : "none",
            display: "inline-flex",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="7" y1="0" x2="7" y2="14" stroke="currentColor" strokeWidth="1" />
            <line x1="0" y1="7" x2="14" y2="7" stroke="currentColor" strokeWidth="1" />
          </svg>
        </span>
      </button>
      {open && (
        <p
          id={panelId}
          style={{
            fontFamily: MONO,
            fontSize: 12,
            fontWeight: 300,
            color: MUTED,
            lineHeight: 1.8,
            letterSpacing: "0.04em",
            paddingBottom: 20,
          }}
        >
          {a}
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN
───────────────────────────────────────────────────────────*/
export default function LandingPage() {
  const { t, lang } = useLang();
  const proPrice = priceFor(lang);
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    html.style.setProperty("overflow", "auto", "important");
    html.style.setProperty("height", "auto", "important");
    body.style.setProperty("overflow", "auto", "important");
    body.style.setProperty("height", "auto", "important");
    if (root) {
      root.style.setProperty("overflow", "auto", "important");
      root.style.setProperty("height", "auto", "important");
    }
    return () => {
      html.style.removeProperty("overflow");
      html.style.removeProperty("height");
      body.style.removeProperty("overflow");
      body.style.removeProperty("height");
      if (root) {
        root.style.removeProperty("overflow");
        root.style.removeProperty("height");
      }
    };
  }, []);

  const [waitlistCount, setWaitlistCount] = useState(0);
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { count, error } = await supabase.from("waitlist").select("*", { count: "exact", head: true });
        if (!error && count !== null) {
          setWaitlistCount(count);
        }
      } catch (e) {
        console.error("waitlist count error:", e);
      }
    };
    fetchCount();
  }, []);

  const [displayCount, setDisplayCount] = useState(0);
  const counterRef = useRef<HTMLDivElement>(null);
  const counterAnimated = useRef(false);

  useEffect(() => {
    const el = counterRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !counterAnimated.current && waitlistCount > 0) {
          counterAnimated.current = true;
          const target = waitlistCount;
          let current = 0;
          const increment = Math.max(target / 72, 1);
          const interval = setInterval(() => {
            current += increment;
            if (current >= target) {
              current = target;
              clearInterval(interval);
            }
            setDisplayCount(Math.floor(current));
          }, 16);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [waitlistCount]);

  const revProv = useReveal(0);
  const revStep1 = useReveal(0);
  const revStep2 = useReveal(120);
  const revStep3 = useReveal(240);
  const revLayouts = useReveal(0);
  const revReferral = useReveal(0);
  const revPlans0 = useReveal(0);
  const revPlans1 = useReveal(100);
  const revPlans2 = useReveal(200);
  const revFaq = useReveal(0);
  const revClosing = useReveal(0);

  const secLabel = (label: string) => (
    <div style={{ marginBottom: 48 }}>
      <span
        style={{ fontFamily: MONO, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: MUTED }}
      >
        {label}
      </span>
      <div style={{ width: 24, height: "0.5px", background: MUTED, marginTop: 8 }} />
    </div>
  );

  const sec: React.CSSProperties = { padding: "96px 60px", borderBottom: BORDER, fontFamily: MONO };

  return (
    <div style={{ background: BG, color: INK, fontFamily: MONO, minHeight: "100vh" }}>
      <PageMeta title={t('landing.docTitle') as string} description={t('landing.metaDesc') as string} />

      <style>{`
  /* ── Cascata: faixas descendo em sequência ── */
  @keyframes cascata-1 {
    0%,100% { transform:translateY(-6px); opacity:0.04; }
    20%,50% { transform:translateY(0);    opacity:0.16; }
    70%     { transform:translateY(6px);  opacity:0.04; }
  }
  @keyframes cascata-2 {
    0%,15%,100% { transform:translateY(-6px); opacity:0.04; }
    35%,60%     { transform:translateY(0);    opacity:0.14; }
    80%         { transform:translateY(6px);  opacity:0.04; }
  }
  @keyframes cascata-3 {
    0%,30%,100% { transform:translateY(-6px); opacity:0.04; }
    50%,75%     { transform:translateY(0);    opacity:0.12; }
    90%         { transform:translateY(6px);  opacity:0.04; }
  }

  /* ── Panorama: colunas deslizando da direita pra esquerda ── */
  @keyframes panorama-1 {
    0%,100% { transform:translateX(14px);  opacity:0.04; }
    25%,55% { transform:translateX(0);     opacity:0.16; }
    75%     { transform:translateX(-14px); opacity:0.04; }
  }
  @keyframes panorama-2 {
    0%,12%,100% { transform:translateX(14px);  opacity:0.04; }
    37%,65%     { transform:translateX(0);     opacity:0.14; }
    85%         { transform:translateX(-14px); opacity:0.04; }
  }
  @keyframes panorama-3 {
    0%,25%,100% { transform:translateX(14px);  opacity:0.04; }
    50%,75%     { transform:translateX(0);     opacity:0.12; }
    92%         { transform:translateX(-14px); opacity:0.04; }
  }

  /* ── Dividida: lados alternando destaque ── */
  @keyframes dividida-left {
    0%,100% { opacity:0.14; }
    45%,55% { opacity:0.04; }
  }
  @keyframes dividida-right {
    0%,100% { opacity:0.04; }
    45%,55% { opacity:0.14; }
  }

  /* ── Mosaico: blocos aparecendo em sequência ── */
  @keyframes mosaico-top {
    0%,100% { opacity:0.04; }
    15%,45% { opacity:0.16; }
    60%     { opacity:0.04; }
  }
  @keyframes mosaico-bl {
    0%,30%,100% { opacity:0.04; }
    45%,70%     { opacity:0.14; }
    85%         { opacity:0.04; }
  }
  @keyframes mosaico-br {
    0%,50%,100% { opacity:0.04; }
    65%,85%     { opacity:0.12; }
    95%         { opacity:0.04; }
  }

  /* ── Magazine: colunas editoriais ── */
  @keyframes magazine-left {
    0%,100% { opacity:0.04; transform:scaleY(0.95); }
    30%,60% { opacity:0.14; transform:scaleY(1); }
    80%     { opacity:0.04; transform:scaleY(0.95); }
  }
  @keyframes magazine-tr {
    0%,20%,100% { opacity:0.04; transform:translateY(4px); }
    40%,65%     { opacity:0.16; transform:translateY(0); }
    85%         { opacity:0.04; transform:translateY(4px); }
  }
  @keyframes magazine-br {
    0%,35%,100% { opacity:0.04; transform:translateY(-4px); }
    55%,80%     { opacity:0.12; transform:translateY(0); }
    95%         { opacity:0.04; transform:translateY(-4px); }
  }

  /* ── Lista: linhas aparecendo em sequência ── */
  @keyframes lista-1 {
    0%,100% { opacity:0.04; transform:scaleX(0.7); transform-origin:left; }
    15%,45% { opacity:0.16; transform:scaleX(1); }
    60%     { opacity:0.04; transform:scaleX(0.7); }
  }
  @keyframes lista-2 {
    0%,15%,100% { opacity:0.04; transform:scaleX(0.7); transform-origin:left; }
    30%,55%     { opacity:0.14; transform:scaleX(1); }
    70%         { opacity:0.04; transform:scaleX(0.7); }
  }
  @keyframes lista-3 {
    0%,30%,100% { opacity:0.04; transform:scaleX(0.7); transform-origin:left; }
    45%,70%     { opacity:0.12; transform:scaleX(1); }
    85%         { opacity:0.04; transform:scaleX(0.7); }
  }
  @keyframes lista-4 {
    0%,45%,100% { opacity:0.04; transform:scaleX(0.7); transform-origin:left; }
    60%,82%     { opacity:0.10; transform:scaleX(1); }
    95%         { opacity:0.04; transform:scaleX(0.7); }
  }

  /* ── Grid Livre: blocos em posições assimétricas ── */
  @keyframes gridlivre-1 {
    0%,100% { opacity:0.04; transform:translate(2px,2px); }
    25%,50% { opacity:0.16; transform:translate(0,0); }
    70%     { opacity:0.04; transform:translate(-2px,-2px); }
  }
  @keyframes gridlivre-2 {
    0%,15%,100% { opacity:0.04; transform:translate(-2px,3px); }
    35%,60%     { opacity:0.14; transform:translate(0,0); }
    80%         { opacity:0.04; transform:translate(2px,-3px); }
  }
  @keyframes gridlivre-3 {
    0%,30%,100% { opacity:0.04; transform:translate(3px,-2px); }
    50%,72%     { opacity:0.12; transform:translate(0,0); }
    90%         { opacity:0.04; transform:translate(-3px,2px); }
  }
  @keyframes gridlivre-4 {
    0%,40%,100% { opacity:0.04; transform:translate(-2px,-2px); }
    58%,82%     { opacity:0.10; transform:translate(0,0); }
    95%         { opacity:0.04; transform:translate(2px,2px); }
  }

  /* ── Timeline: blocos laterais aparecendo ── */
  @keyframes timeline-1 {
    0%,100% { opacity:0.04; transform:translateX(-6px); }
    20%,50% { opacity:0.16; transform:translateX(0); }
    70%     { opacity:0.04; transform:translateX(-6px); }
  }
  @keyframes timeline-2 {
    0%,20%,100% { opacity:0.04; transform:translateX(6px); }
    40%,65%     { opacity:0.14; transform:translateX(0); }
    85%         { opacity:0.04; transform:translateX(6px); }
  }
  @keyframes timeline-3 {
    0%,40%,100% { opacity:0.04; transform:translateX(-6px); }
    58%,80%     { opacity:0.12; transform:translateX(0); }
    95%         { opacity:0.04; transform:translateX(-6px); }
  }
      `}</style>
      <PublicNav />

      {/* 01 — HERO */}
      <HeroSection waitlistCount={waitlistCount} />

      {/* TICKER */}
      <Ticker />

      {/* 02 — PROVOCAÇÃO */}
      <section style={sec}>
        <div
          ref={revProv.ref}
          style={{
            ...revProv.style,
            maxWidth: 900,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 64,
          }}
        >
          <div>
            {(t('landing.provocacao.lines') as string[]).map((line, i, arr) => {
              const dim = i < arr.length - 1;
              return (
                <p
                  key={i}
                  style={{
                    fontFamily: MONO,
                    fontSize: "clamp(16px, 3vw, 28px)",
                    fontWeight: 300,
                    lineHeight: 1.6,
                    color: dim ? MUTED : INK,
                    borderBottom: i < arr.length - 1 ? BORDER : "none",
                    paddingBottom: i < arr.length - 1 ? 16 : 0,
                    marginBottom: i < arr.length - 1 ? 16 : 0,
                  }}
                >
                  {line}
                </p>
              );
            })}
          </div>

          <div>
            <p
              style={{
                fontFamily: MONO,
                fontSize: "clamp(20px, 4vw, 36px)",
                fontWeight: 300,
                lineHeight: 1.4,
                color: INK,
                whiteSpace: "pre-line",
              }}
            >
              {t('landing.provocacao.headline')}
            </p>
            <p
              style={{
                fontFamily: MONO,
                fontSize: 14,
                fontWeight: 300,
                color: BODY,
                lineHeight: 1.8,
                letterSpacing: "0.04em",
                maxWidth: 480,
                marginTop: 16,
              }}
            >
              {t('landing.provocacao.body')}
            </p>
          </div>
        </div>
      </section>

      {/* 03 — COMO FUNCIONA */}
      <section id="como" style={sec}>
        {secLabel(t('landing.como.label'))}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 48,
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          {[
            {
              num: "01",
              title: (t('landing.como.steps') as any)[0].title,
              desc: (t('landing.como.steps') as any)[0].desc,
              reveal: revStep1,
              icon: (
                <svg width="72" height="56" viewBox="0 0 72 56" fill="none">
                  <rect
                    x="1"
                    y="1"
                    width="70"
                    height="14"
                    fill="rgba(10,10,10,0.06)"
                    stroke="rgba(10,10,10,0.2)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.1s" }}
                  />
                  <circle
                    cx="8"
                    cy="8"
                    r="2.5"
                    fill="rgba(10,10,10,0.15)"
                    className="geo-shape"
                    style={{ animationDelay: "0.2s" }}
                  />
                  <circle
                    cx="16"
                    cy="8"
                    r="2.5"
                    fill="rgba(10,10,10,0.1)"
                    className="geo-shape"
                    style={{ animationDelay: "0.25s" }}
                  />
                  <circle
                    cx="24"
                    cy="8"
                    r="2.5"
                    fill="rgba(10,10,10,0.08)"
                    className="geo-shape"
                    style={{ animationDelay: "0.3s" }}
                  />
                  <rect
                    x="32"
                    y="4"
                    width="34"
                    height="6"
                    fill="rgba(10,10,10,0.06)"
                    stroke="rgba(10,10,10,0.15)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.35s" }}
                  />
                  <rect
                    x="1"
                    y="19"
                    width="70"
                    height="36"
                    fill="rgba(10,10,10,0.04)"
                    stroke="rgba(10,10,10,0.15)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.45s" }}
                  />
                  <rect
                    x="8"
                    y="26"
                    width="56"
                    height="8"
                    fill="rgba(10,10,10,0.08)"
                    className="geo-shape"
                    style={{ animationDelay: "0.55s" }}
                  />
                  <rect
                    x="8"
                    y="38"
                    width="36"
                    height="6"
                    fill="rgba(10,10,10,0.05)"
                    className="geo-shape"
                    style={{ animationDelay: "0.65s" }}
                  />
                </svg>
              ),
            },
            {
              num: "02",
              title: (t('landing.como.steps') as any)[1].title,
              desc: (t('landing.como.steps') as any)[1].desc,
              reveal: revStep2,
              icon: (
                <svg width="72" height="56" viewBox="0 0 72 56" fill="none">
                  <rect
                    x="1"
                    y="1"
                    width="32"
                    height="25"
                    fill="rgba(10,10,10,0.08)"
                    stroke="rgba(10,10,10,0.2)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.1s" }}
                  />
                  <rect
                    x="37"
                    y="1"
                    width="34"
                    height="25"
                    fill="rgba(10,10,10,0.06)"
                    stroke="rgba(10,10,10,0.2)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.2s" }}
                  />
                  <rect
                    x="1"
                    y="30"
                    width="20"
                    height="25"
                    fill="rgba(10,10,10,0.05)"
                    stroke="rgba(10,10,10,0.2)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.3s" }}
                  />
                  <rect
                    x="25"
                    y="30"
                    width="24"
                    height="25"
                    fill="rgba(10,10,10,0.07)"
                    stroke="rgba(10,10,10,0.2)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.4s" }}
                  />
                  <rect
                    x="53"
                    y="30"
                    width="18"
                    height="25"
                    fill="rgba(10,10,10,0.04)"
                    stroke="rgba(10,10,10,0.2)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.5s" }}
                  />
                </svg>
              ),
            },
            {
              num: "03",
              title: (t('landing.como.steps') as any)[2].title,
              desc: (t('landing.como.steps') as any)[2].desc,
              reveal: revStep3,
              icon: (
                <svg width="72" height="56" viewBox="0 0 72 56" fill="none">
                  <circle
                    cx="36"
                    cy="28"
                    r="10"
                    fill="rgba(10,10,10,0.08)"
                    stroke="rgba(10,10,10,0.2)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.1s" }}
                  />
                  <circle
                    cx="8"
                    cy="10"
                    r="6"
                    fill="rgba(10,10,10,0.06)"
                    stroke="rgba(10,10,10,0.15)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.2s" }}
                  />
                  <circle
                    cx="64"
                    cy="10"
                    r="6"
                    fill="rgba(10,10,10,0.06)"
                    stroke="rgba(10,10,10,0.15)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.3s" }}
                  />
                  <circle
                    cx="8"
                    cy="46"
                    r="6"
                    fill="rgba(10,10,10,0.05)"
                    stroke="rgba(10,10,10,0.15)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.4s" }}
                  />
                  <circle
                    cx="64"
                    cy="46"
                    r="6"
                    fill="rgba(10,10,10,0.05)"
                    stroke="rgba(10,10,10,0.15)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.5s" }}
                  />
                  <line
                    x1="27"
                    y1="22"
                    x2="14"
                    y2="14"
                    stroke="rgba(10,10,10,0.12)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.25s" }}
                  />
                  <line
                    x1="45"
                    y1="22"
                    x2="58"
                    y2="14"
                    stroke="rgba(10,10,10,0.12)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.35s" }}
                  />
                  <line
                    x1="27"
                    y1="34"
                    x2="14"
                    y2="42"
                    stroke="rgba(10,10,10,0.12)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.45s" }}
                  />
                  <line
                    x1="45"
                    y1="34"
                    x2="58"
                    y2="42"
                    stroke="rgba(10,10,10,0.12)"
                    strokeWidth="0.5"
                    className="geo-shape"
                    style={{ animationDelay: "0.55s" }}
                  />
                </svg>
              ),
            },
          ].map((step) => (
            <div key={step.num} ref={step.reveal.ref} style={{ ...step.reveal.style }}>
              <span style={{ fontFamily: MONO, fontSize: 12, letterSpacing: "0.18em", color: MUTED }}>{step.num}</span>
              <div style={{ margin: "16px 0" }}>{step.icon}</div>
              <h3
                style={{
                  fontFamily: MONO,
                  fontSize: 14,
                  fontWeight: 400,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 8,
                }}
              >
                {step.title}
              </h3>
              <p
                style={{
                  fontFamily: MONO,
                  fontSize: 12,
                  fontWeight: 300,
                  color: BODY,
                  lineHeight: 1.8,
                  letterSpacing: "0.04em",
                }}
              >
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 04 — LAYOUTS */}
      <section ref={revLayouts.ref} style={{ ...sec, ...revLayouts.style }}>
        {secLabel(t('landing.layouts.label'))}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 32,
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          {[
            {
              letter: "A",
              name: "Cascata",
              desc: "Rolagem vertical, uma imagem por vez. contemplativo.",
              available: true,
              preview: (
                <svg width="64" height="48" viewBox="0 0 64 48" fill="none" opacity="0.45">
                  <rect x="16" y="1" width="32" height="14" fill={INK} style={{ animation: "cascata-1 3s ease-in-out infinite" }} />
                  <rect x="16" y="17" width="32" height="14" fill={INK} style={{ animation: "cascata-2 3s ease-in-out infinite" }} />
                  <rect x="16" y="33" width="32" height="14" fill={INK} style={{ animation: "cascata-3 3s ease-in-out infinite" }} />
                </svg>
              ),
            },
            {
              letter: "B",
              name: "Panorama",
              desc: "Rolagem horizontal, filmstrip. sensação de exposição.",
              available: true,
              preview: (
                <svg width="64" height="48" viewBox="0 0 64 48" fill="none" opacity="0.45">
                  <rect x="1" y="12" width="20" height="24" fill={INK} style={{ animation: "panorama-1 3.5s ease-in-out infinite" }} />
                  <rect x="23" y="12" width="20" height="24" fill={INK} style={{ animation: "panorama-2 3.5s ease-in-out infinite" }} />
                  <rect x="45" y="12" width="20" height="24" fill={INK} style={{ animation: "panorama-3 3.5s ease-in-out infinite" }} />
                </svg>
              ),
            },
            {
              letter: "C",
              name: "Dividida",
              desc: "Duas colunas lado a lado. ideal para contrastar séries.",
              available: true,
              preview: (
                <svg width="64" height="48" viewBox="0 0 64 48" fill="none" opacity="0.45">
                  <rect x="1" y="1" width="28" height="46" fill={INK} style={{ animation: "dividida-left 4s ease-in-out infinite" }} />
                  <rect x="35" y="1" width="28" height="46" fill={INK} style={{ animation: "dividida-right 4s ease-in-out infinite" }} />
                </svg>
              ),
            },
            {
              letter: "D",
              name: "Mosaico",
              desc: "Faixas de alturas variadas. hierarquia visual dinâmica.",
              available: true,
              preview: (
                <svg width="64" height="48" viewBox="0 0 64 48" fill="none" opacity="0.45">
                  <rect x="1" y="1" width="62" height="14" fill={INK} style={{ animation: "mosaico-top 3.5s ease-in-out infinite" }} />
                  <rect x="1" y="17" width="30" height="30" fill={INK} style={{ animation: "mosaico-bl 3.5s ease-in-out infinite" }} />
                  <rect x="33" y="17" width="30" height="14" fill={INK} style={{ animation: "mosaico-br 3.5s ease-in-out infinite" }} />
                  <rect x="33" y="33" width="30" height="14" fill={INK} style={{ animation: "mosaico-top 3.5s ease-in-out infinite", animationDelay: "0.8s" }} />
                </svg>
              ),
            },
            {
              letter: "E",
              name: "Magazine",
              desc: "Colunas editoriais com hierarquia de tamanhos.",
              available: false,
              preview: (
                <svg width="64" height="48" viewBox="0 0 64 48" fill="none" opacity="0.45">
                  <rect x="1" y="1" width="28" height="46" fill={INK} style={{ animation: "magazine-left 3.5s ease-in-out infinite" }} />
                  <rect x="35" y="1" width="28" height="22" fill={INK} style={{ animation: "magazine-tr 3.5s ease-in-out infinite" }} />
                  <rect x="35" y="25" width="28" height="22" fill={INK} style={{ animation: "magazine-br 3.5s ease-in-out infinite" }} />
                </svg>
              ),
            },
            {
              letter: "F",
              name: "Lista",
              desc: "Obras em lista linear com título e descrição.",
              available: false,
              preview: (
                <svg width="64" height="48" viewBox="0 0 64 48" fill="none" opacity="0.45">
                  <rect x="4" y="4" width="56" height="6" fill={INK} style={{ animation: "lista-1 3s ease-in-out infinite" }} />
                  <rect x="4" y="14" width="44" height="6" fill={INK} style={{ animation: "lista-2 3s ease-in-out infinite" }} />
                  <rect x="4" y="24" width="52" height="6" fill={INK} style={{ animation: "lista-3 3s ease-in-out infinite" }} />
                  <rect x="4" y="34" width="38" height="6" fill={INK} style={{ animation: "lista-4 3s ease-in-out infinite" }} />
                </svg>
              ),
            },
            {
              letter: "G",
              name: "Grid Livre",
              desc: "Posicionamento livre por arrasto. controle total.",
              available: false,
              preview: (
                <svg width="64" height="48" viewBox="0 0 64 48" fill="none" opacity="0.45">
                  <rect x="2" y="2" width="18" height="18" fill={INK} style={{ animation: "gridlivre-1 4s ease-in-out infinite" }} />
                  <rect x="24" y="8" width="14" height="26" fill={INK} style={{ animation: "gridlivre-2 4s ease-in-out infinite" }} />
                  <rect x="42" y="4" width="20" height="14" fill={INK} style={{ animation: "gridlivre-3 4s ease-in-out infinite" }} />
                  <rect x="44" y="24" width="16" height="20" fill={INK} style={{ animation: "gridlivre-4 4s ease-in-out infinite" }} />
                </svg>
              ),
            },
            {
              letter: "H",
              name: "Timeline",
              desc: "Obras ordenadas por data. ideal para mostrar evolução.",
              available: false,
              preview: (
                <svg width="64" height="48" viewBox="0 0 64 48" fill="none" opacity="0.45">
                  <line x1="32" y1="2" x2="32" y2="46" stroke={INK} strokeWidth="0.5" />
                  <rect x="6" y="6" width="22" height="8" fill={INK} style={{ animation: "timeline-1 3.5s ease-in-out infinite" }} />
                  <rect x="36" y="20" width="22" height="8" fill={INK} style={{ animation: "timeline-2 3.5s ease-in-out infinite" }} />
                  <rect x="6" y="34" width="22" height="8" fill={INK} style={{ animation: "timeline-3 3.5s ease-in-out infinite" }} />
                </svg>
              ),
            },
          ].map((l, idx) => {
            const lt = (t('landing.layouts.items') as Array<{name:string;desc:string}>)[idx] || { name: l.name, desc: l.desc };
            return (
            <div key={l.letter} style={{ display: "flex", flexDirection: "column", gap: 12, opacity: 1, pointerEvents: l.available ? "auto" as const : "none" as const, position: "relative" }}>
              {!l.available && (
                <span style={{ position: "absolute", top: 6, right: 6, fontFamily: MONO, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: MUTED, border: `0.5px solid ${MUTED}`, padding: "2px 6px", zIndex: 1 }}>{t('landing.layouts.soon')}</span>
              )}
              <div style={{ display: "flex", justifyContent: "center", padding: "16px 0", borderBottom: BORDER }}>
                {l.preview}
              </div>
              <div>
                <span style={{ fontFamily: MONO, fontSize: 12, letterSpacing: "0.18em", color: MUTED }}>
                  {l.letter}
                </span>
                <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 400, marginLeft: 8 }}>{lt.name}</span>
                <p
                  style={{
                    fontFamily: MONO,
                    fontSize: 12,
                    fontWeight: 300,
                    color: BODY,
                    lineHeight: 1.7,
                    marginTop: 4,
                  }}
                >
                  {lt.desc}
                </p>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* 05 — REFERRAL */}
      <section ref={revReferral.ref} style={{ ...sec, ...revReferral.style }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 64,
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          <div>
            <p
              style={{
                fontFamily: MONO,
                fontSize: "clamp(48px, 10vw, 96px)",
                fontWeight: 300,
                lineHeight: 1,
                color: INK,
              }}
            >
              5
            </p>
            <p
              style={{
                fontFamily: MONO,
                fontSize: "clamp(18px, 3vw, 28px)",
                fontWeight: 300,
                lineHeight: 1.4,
                color: INK,
                marginTop: 8,
              }}
            >
              <span style={{ whiteSpace: 'pre-line' }}>{t('landing.referral.headline')}</span>
            </p>
            <p
              style={{
                fontFamily: MONO,
                fontSize: 12,
                fontWeight: 300,
                color: BODY,
                lineHeight: 1.8,
                letterSpacing: "0.04em",
                marginTop: 16,
                maxWidth: 360,
              }}
            >
              {t('landing.referral.body')}
            </p>
          </div>
          <div>
            <p
              style={{
                fontFamily: MONO,
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: MUTED,
                marginBottom: 24,
              }}
            >
              {t('landing.referral.how_label')}
            </p>
            {(t('landing.referral.steps') as string[]).map((step, i) => ({ n: String(i+1).padStart(2,'0'), t: step })).map((s, i, arr) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 16,
                  padding: "14px 0",
                  borderBottom: i < arr.length - 1 ? BORDER : "none",
                }}
              >
                <span style={{ fontFamily: MONO, fontSize: 12, color: MUTED, minWidth: 20 }}>{s.n}</span>
                <p
                  style={{
                    fontFamily: MONO,
                    fontSize: 12,
                    fontWeight: 300,
                    color: BODY,
                    lineHeight: 1.7,
                    letterSpacing: "0.04em",
                  }}
                >
                  {s.t}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 06 — PLANOS */}
      <section id="planos" style={sec}>
        {secLabel(t('landing.planos.label'))}
        <p
          style={{
            fontFamily: MONO,
            fontSize: 12,
            fontWeight: 300,
            color: BODY,
            lineHeight: 1.8,
            letterSpacing: "0.04em",
            maxWidth: 520,
            marginBottom: 48,
          }}
        >
          {t('landing.planos.intro_a')}
          <span style={{ color: INK }}>{t('landing.planos.intro_b')}</span>{t('landing.planos.intro_c')}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 0,
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          {[
            {
              badge: (t('landing.planos.free.badge') as string),
              name: (t('landing.planos.free.name') as string),
              price: (t('landing.planos.free.price') as string),
              period: (t('landing.planos.free.period') as string),
              featured: false,
              note: "",
              reveal: revPlans0,
              features: [
                ...((t('landing.planos.free.features') as string[]) || []),
                null, null, null,
              ].slice(0, 7) as (string | null)[],
            },
            {
              badge: (t('landing.planos.pro.badge') as string),
              name: (t('landing.planos.pro.name') as string),
              price: proPrice.display,
              period: (t('landing.planos.pro.period') as string),
              featured: true,
              note: (t('landing.planos.pro.note') as string),
              reveal: revPlans1,
              features: (t('landing.planos.pro.features') as string[]),
            },
            {
              badge: (t('landing.planos.premium.badge') as string),
              name: (t('landing.planos.premium.name') as string),
              price: (t('landing.planos.premium.price') as string),
              period: (t('landing.planos.premium.period') as string),
              featured: false,
              note: "",
              reveal: revPlans2,
              features: (t('landing.planos.premium.features') as string[]),
            },
          ].map((plan) => {
            const fg = plan.featured;
            const clr = (v: boolean) => (v ? (fg ? "rgba(247,246,242,0.4)" : MUTED) : fg ? BG : INK);
            return (
              <div
                key={plan.name}
                ref={plan.reveal.ref}
                style={{
                  ...plan.reveal.style,
                  padding: "40px 32px",
                  background: fg ? INK : "transparent",
                  color: fg ? BG : INK,
                  borderRight: BORDER,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <span
                  style={{
                    fontFamily: MONO,
                    fontSize: 12,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: clr(true),
                    marginBottom: 24,
                  }}
                >
                  {plan.badge}
                </span>
                <p style={{ fontFamily: MONO, fontSize: 18, fontWeight: 400, marginBottom: 4 }}>{plan.name}</p>
                <p style={{ fontFamily: MONO, fontSize: 32, fontWeight: 300, marginBottom: 4 }}>{plan.price}</p>
                <p
                  style={{
                    fontFamily: MONO,
                    fontSize: 12,
                    color: clr(true),
                    letterSpacing: "0.14em",
                    marginBottom: 32,
                  }}
                >
                  {plan.period}
                </p>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                  {plan.features.map((f, fi) => (
                    <span
                      key={fi}
                      style={{
                        fontFamily: MONO,
                        fontSize: 12,
                        fontWeight: 300,
                        color: clr(!f),
                        lineHeight: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {f && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M2 5.5L4 7.5L8 3" stroke="currentColor" strokeWidth="1" />
                        </svg>
                      )}
                      {f || "—"}
                    </span>
                  ))}
                </div>
                {plan.badge === (t('landing.planos.premium.badge') as string) ? (
                  <a
                    href="mailto:oi@criaaa.com"
                    style={{
                      marginTop: 36,
                      fontFamily: MONO,
                      fontSize: 12,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase" as const,
                      fontWeight: 400,
                      padding: "14px 24px",
                      border: "0.5px solid rgba(10,10,10,0.25)",
                      background: "transparent",
                      color: MUTED,
                      cursor: "pointer",
                      width: "100%",
                      display: "block",
                      textAlign: "center" as const,
                      textDecoration: "none",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = INK;
                      (e.currentTarget as HTMLAnchorElement).style.color = BG;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                      (e.currentTarget as HTMLAnchorElement).style.color = MUTED;
                    }}
                  >
                    {t('landing.planos.cta_contato')}
                  </a>
                ) : (
                  <button
                    onClick={() => document.getElementById("hero")?.scrollIntoView({ behavior: "smooth" })}
                    onMouseEnter={(e) => {
                      const b = e.currentTarget;
                      b.style.background = fg ? BG : INK;
                      b.style.color = fg ? INK : BG;
                    }}
                    onMouseLeave={(e) => {
                      const b = e.currentTarget;
                      b.style.background = fg ? "rgba(247,246,242,0.08)" : "transparent";
                      b.style.color = fg ? BG : MUTED;
                    }}
                    style={{
                      marginTop: 36,
                      fontFamily: MONO,
                      fontSize: 12,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      fontWeight: 400,
                      padding: "14px 24px",
                      border: `0.5px solid ${fg ? "rgba(247,246,242,0.25)" : "rgba(10,10,10,0.25)"}`,
                      background: fg ? "rgba(247,246,242,0.08)" : "transparent",
                      color: fg ? BG : MUTED,
                      cursor: "pointer",
                      width: "100%",
                      transition: "all 0.15s",
                    }}
                  >
                    {t('landing.planos.cta_garantir')}
                  </button>
                )}
                {plan.note && (
                  <p style={{ fontFamily: MONO, fontSize: 12, color: clr(true), marginTop: 12, textAlign: "center" }}>
                    {plan.note}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 07 — FAQ */}
      <section id="faq" ref={revFaq.ref} style={{ ...sec, ...revFaq.style }}>
        {secLabel(t('landing.faq.label'))}
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div>
            {(t('landing.faq.items') as Array<{q:string;a:string}>).map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* 08 — CLOSING */}
      <section ref={revClosing.ref} style={{ ...revClosing.style, padding: "120px 60px", textAlign: "center" }}>
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <p
            style={{
              fontFamily: MONO,
              fontSize: "clamp(24px, 5vw, 40px)",
              fontWeight: 300,
              lineHeight: 1.3,
              color: INK,
              marginBottom: 48,
              whiteSpace: "pre-line",
            }}
          >
            {t('landing.closing.headline')}
          </p>

          <p style={{ fontFamily: MONO, fontSize: 12, letterSpacing: "0.14em", color: MUTED, marginBottom: 40 }}>
            {t('landing.closing.sub')}
          </p>

          <div className="hide-mobile">
            <WaitlistForm waitlistCount={waitlistCount} />
          </div>
        </div>
      </section>

      <PublicFooter />
      <div style={{ height: 80 }} />
      <BottomBar />
    </div>
  );
}
